/* Launch sequence. Runs once, before the first render.
 *
 * Order matters: identity, then persistence, then the engine. Persistence is
 * re-requested on every launch rather than once, because Safari drops the
 * grant and an un-persisted origin is one storage-pressure event away from
 * losing a day's work. Cheap to ask; catastrophic to skip.
 */

import { DEMO_USER_ID, uuidv7, type KilroySession } from '@kilroy/shared';
import { db, getMeta, setMeta } from './db/db';
import { ensurePersisted, type StorageReport } from './media/opfs';
import { setAccessToken, startEngine } from './sync/engine';

export interface BootState {
  deviceId: string;
  userId: string;
  /** The signed-in session, restored from the device. Null means sign in. */
  session: KilroySession | null;
  storage: StorageReport;
  /** True when launched from the home screen rather than a browser tab. */
  standalone: boolean;
  onboarded: boolean;
}

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS predates the display-mode query and still needs this.
  (navigator as unknown as { standalone?: boolean }).standalone === true;

export async function boot(): Promise<BootState> {
  let deviceId = await getMeta<string>('device_id', '');
  if (!deviceId) {
    // Generated once at install and carried on every push, for idempotency
    // and audit. If this changes, the server sees a new device — so it must
    // outlive everything except a full reinstall.
    deviceId = uuidv7();
    await setMeta('device_id', deviceId);
  }

  /* The session lives in IndexedDB, not localStorage.
   *
   * Not a preference — Safari evicts localStorage under storage pressure on a
   * PWA that has been backgrounded, and being signed out on a ridge is exactly
   * the failure this app exists to prevent. IndexedDB sits behind the same
   * persistence grant as the evidence itself, so the session survives whatever
   * the evidence survives.
   *
   * The user id comes FROM the session. It used to default to a roster slug
   * while every record in the database carries a uuid, so every "mine" filter
   * matched nothing and a synced phone looked empty. */
  const session = await getMeta<KilroySession | null>('session', null);
  setAccessToken(session?.accessToken ?? null);
  const userId = session?.userId ?? (await getMeta<string>('user_id', DEMO_USER_ID));
  const onboarded = await getMeta<boolean>('onboarded', false);
  const storage = await ensurePersisted();

  startEngine();

  return { deviceId, userId, session, storage, standalone: isStandalone(), onboarded };
}

/** Dev reset. Wipes the device store but never touches OPFS media without
 *  saying so — the files are the one thing that cannot be re-derived. */
export async function resetDevice(includeMedia = false): Promise<void> {
  await db.delete();
  if (includeMedia) {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry('media', { recursive: true }).catch(() => {});
  }
  location.reload();
}


/** Persist a session, and adopt its identity. */
export async function saveSession(session: KilroySession): Promise<void> {
  await setMeta('session', session);
  await setMeta('user_id', session.userId);
  setAccessToken(session.accessToken);
}

/**
 * Sign out.
 *
 * Refuses while anything is queued. The phone holds the only copy of an unsent
 * photograph, and signing out would destroy evidence — the one thing this
 * product must never do. Settings already applies the same rule; it lives here
 * too so no future caller can route around it.
 */
export async function clearSession(): Promise<{ ok: boolean; queued: number }> {
  const queued = await db.outbox.count();
  if (queued > 0) return { ok: false, queued };
  await setMeta('session', null);
  setAccessToken(null);
  return { ok: true, queued: 0 };
}
